/**
 * Utilidad para obtener tokens de autenticación de forma consistente
 * 
 * Estrategia:
 * 1. Intenta obtener token fresco de Firebase (si hay usuario autenticado)
 * 2. Si falla, usa token del localStorage como fallback
 * 
 * Esto asegura que siempre se use el token más reciente y válido.
 */

import { auth } from '@/firebase';
import { isTokenValid } from '@/utils/tokenValidation';

/**
 * Obtiene el token de autenticación del usuario actual.
 * 
 * Prioridad:
 * 1. Token fresco de Firebase (si el usuario está autenticado)
 * 2. Token del localStorage (JWT interno como fallback)
 * 
 * @param forceRefresh - Si es true, fuerza la renovación del token de Firebase
 * @returns Promise con el token o null si no hay token disponible
 */
const IMPERSONATION_TOKEN_KEY = 'impersonation_token';

export async function getAuthToken(forceRefresh: boolean = true): Promise<string | null> {
  try {
    // PRIORIDAD 0: Token de impersonación (admin viendo como otro usuario)
    if (typeof sessionStorage !== 'undefined') {
      const impToken = sessionStorage.getItem(IMPERSONATION_TOKEN_KEY);
      if (impToken && impToken.trim().length > 0) return impToken;
    }

    // PRIORIDAD 1: Token del localStorage (JWT interno del backend)
    // Este token se obtiene tras el login exitoso en /tokens/google y es el que el backend espera/valida mejor.
    const storedToken = localStorage.getItem('user_token');
    if (storedToken && storedToken.trim().length > 0) {
      // Opcional: Podríamos verificar expiración aquí con jwt-decode si quisiéramos ser proactivos
      console.log(' Usando token del localStorage (Backend JWT)');
      return storedToken;
    }

    // PRIORIDAD 2: Fallback a token de Firebase (si no hay token de backend)
    // Útil si el backend aceptara directamente tokens de Firebase (y estuviera configurado para ello)
    const user = auth.currentUser;
    if (user) {
      try {
        // Obtener token fresco de Firebase
        const token = await user.getIdToken(forceRefresh);
        if (token && token.trim().length > 0) {
          console.log(' Token fresco obtenido de Firebase (Fallback)');
          return token;
        }
      } catch (firebaseError) {
        console.warn(' Error obteniendo token de Firebase:', firebaseError);
      }
    }

    return null;
  } catch (error) {
    console.error(' Error obteniendo token de autenticación:', error);

    // Último fallback: intentar obtener del localStorage
    const storedToken = localStorage.getItem('user_token');
    return (storedToken && storedToken.trim().length > 0) ? storedToken : null;
  }
}

/**
 * Verifica si hay un usuario autenticado en Firebase
 */
export function isUserAuthenticated(): boolean {
  return auth.currentUser !== null;
}

/**
 * Token de impersonación (admin): clave en sessionStorage
 */
export function getImpersonationTokenKey(): string {
  return IMPERSONATION_TOKEN_KEY;
}

/**
 * Token a usar para autenticación/rol según la ruta (para que ProtectedRoute y HomeRoute coincidan).
 * - /admin: solo localStorage (no impersonación).
 * - Resto: si hay token de impersonación válido, usarlo; si no, localStorage.
 */
export function getTokenForPath(pathname: string): string | null {
  const storageToken = typeof localStorage !== 'undefined' ? localStorage.getItem('user_token') : null;
  if (pathname.startsWith('/admin')) {
    return storageToken;
  }
  const impToken = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(IMPERSONATION_TOKEN_KEY) : null;
  if (impToken && impToken.trim().length > 0 && isTokenValid(impToken)) {
    return impToken;
  }
  return storageToken;
}

/**
 * Limpia el token de impersonación y opcionalmente redirige a /admin
 */
export function clearImpersonation(redirectToAdmin: boolean = true): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY);
    if (redirectToAdmin && typeof window !== 'undefined') window.location.href = '/admin';
  }
}

/**
 * Obtiene el ID del usuario actual (Firebase UID o del token almacenado)
 */
export async function getCurrentUserId(): Promise<string | null> {
  try {
    const user = auth.currentUser;
    if (user) {
      return user.uid;
    }

    // Intentar obtener del token almacenado
    const token = localStorage.getItem('user_token');
    if (token) {
      // Decodificar el token para obtener el user_id
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.firebase_uid || payload.user_id || payload.sub || null;
      } catch {
        // Si no se puede decodificar, retornar null
        return null;
      }
    }

    return null;
  } catch (error) {
    console.error('Error obteniendo ID de usuario:', error);
    return null;
  }
}

